/**
 * RestoID — Reset-password landing (`first_screen=reset_password`, and the target
 * of a password-reset magic link).
 * ---------------------------------------------------------------------------
 * Custom RestoID PRESENTATION on the PRISTINE upstream LOGIC of
 * `src/pages/ResetPasswordLanding` (index + `OneTimeTokenForm`):
 *   - one-time-token / login_hint ... upstream `useConsumeOneTimeTokenParameters`
 *     (which ALSO strips both parameters out of the URL in a layout effect — the
 *     reason this hook is called before every early return, exactly as upstream)
 *   - which identifiers may reset ... upstream `useResetPasswordMethods`
 *   - the pre-filled identifier ..... upstream `usePrefilledIdentifier`
 *     (`isForgotPassword: true` — a user may reset with a different identifier
 *     than they sign in with, so upstream keeps a separate cache)
 *   - magic-link verification ....... upstream `identifyForgotPasswordWithOneTimeToken`
 *     through upstream `useApi`
 *   - send reset code ............... upstream `useSendVerificationCode(ForgotPassword)`
 *   - captcha ....................... upstream `CaptchaBox`
 *
 * Deliberate differences from upstream, and why:
 *  - `FocusedAuthPageLayout` is replaced by `FirstScreenLayout` + `ScreenHead`
 *    (same title and description phrases), and its `authOptionsLink` by a centred
 *    `LinkText` — the shape `custom/pages/SingleSignOnLanding` already uses. No
 *    back pill: this is a FIRST screen, so there may be no history to go back to;
 *    upstream's "back to sign in" link is the way out.
 *  - the head matches `custom/pages/ForgotPassword` (title `description.reset_password`,
 *    the identifier list as the subtitle) so the two reset entry points read alike.
 *  - upstream's `SmartInputField` becomes ONE explicit RestoID control, phone-first
 *    — shared with `custom/pages/IdentifierSignIn` (`IdentifierField`).
 *  - the CTA on a phone identifier says "Получить код" (`getCode`), the same label
 *    `custom/pages/ForgotPassword` uses; every other identifier keeps upstream's
 *    `action.continue`.
 *  - upstream's `required` validation branch is unreachable because the CTA is
 *    disabled until the field is filled; the `invalid` branches are kept verbatim,
 *    including the one-time-token form's `error.invalid_email`.
 *  - upstream's `LoadingLayer` is kept as-is for the auto-submitting magic link:
 *    every API call already raises the same mask through `useApi`, so a bespoke
 *    RestoID spinner here would be the odd one out.
 *  - upstream disables the one-time-token e-mail input while a verification is in
 *    flight; `Field` has no `disabled` prop and `custom/components/restoid.tsx` is
 *    not ours to extend, so the field stays editable and the SUBMIT path is what is
 *    frozen instead (`isVerificationPending` short-circuits `onSubmitHandler` and
 *    disables the CTA) — the double-submit the upstream `disabled` prevents cannot
 *    happen either way.
 *  - the e-mail control keeps upstream's `name="identifier"`, because
 *    `src/pages/ResetPasswordLanding/index.test.tsx` locates the field by exactly
 *    that selector when it asserts the magic-link screen shows no form.
 * Swapped in for `./pages/ResetPasswordLanding` via `src/App.tsx`.
 */
import {
  SignInIdentifier,
  experience,
  type OneTimeTokenVerificationVerifyPayload,
  type RequestErrorBody,
} from '@logto/schemas';
import { HTTPError, TimeoutError } from 'ky';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { identifyForgotPasswordWithOneTimeToken } from '@/apis/experience';
import CaptchaBox from '@/containers/CaptchaBox';
import useApi from '@/hooks/use-api';
import useConsumeOneTimeTokenParameters from '@/hooks/use-consume-one-time-token-parameters';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import useSendVerificationCode from '@/hooks/use-send-verification-code';
import { type ResetPasswordMagicLinkError } from '@/pages/ResetPasswordLanding/types';
import { useResetPasswordMethods } from '@/pages/ResetPasswordLanding/use-reset-password-methods';
import LoadingLayer from '@/shared/components/LoadingLayer';
import { UserFlow, type VerificationCodeIdentifier } from '@/types';
import { identifierInputDescriptionMap, validateIdentifierField } from '@/utils/form';

import { Button, LinkText, ScreenHead, Stack } from '../components/restoid';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';
import { IdentifierField, useIdentifierValue } from './IdentifierSignIn';

/* ------------------------------------------------- the magic-link email form */

type OneTimeTokenFormProps = {
  token: string;
  loginHint?: string;
  onError: (error: ResetPasswordMagicLinkError) => void;
};

/**
 * RestoID restatement of `src/pages/ResetPasswordLanding/OneTimeTokenForm`.
 *
 * Everything load-bearing is preserved: the ONE-SHOT auto-submit when the link
 * carried a `login_hint` (guarded by a ref so a re-render cannot fire it twice),
 * the "render a loading mask instead of the form whenever a hint is present"
 * rule, the frozen form while a verification is in flight, and the full error
 * routing — `guard.invalid_input` becomes `error.invalid_email`, any other HTTP
 * error surfaces the server's raw message, an unparseable body or an unknown
 * failure becomes `error.unknown`, and a `TimeoutError` becomes `error.timeout`.
 * On success the identifier is cached for the next screen and the flow replaces
 * this entry in history so "Back" cannot re-enter a consumed token.
 */
const OneTimeTokenForm = ({ token, loginHint = '', onError }: OneTimeTokenFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigateWithPreservedSearchParams();
  const asyncIdentifyForgotPasswordWithOneTimeToken = useApi(
    identifyForgotPasswordWithOneTimeToken
  );
  const { setForgotPasswordIdentifierInputValue } = useContext(UserInteractionContext);

  const [email, setEmail] = useState('');
  const [invalidMessage, setInvalidMessage] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false);
  const isAutoSubmitted = useRef(false);

  const submit = useCallback(
    async (payload: OneTimeTokenVerificationVerifyPayload) => {
      const [error] = await asyncIdentifyForgotPasswordWithOneTimeToken(payload);

      if (error) {
        if (error instanceof HTTPError) {
          try {
            const { code, message } = await error.response.json<RequestErrorBody>();

            onError(
              code === 'guard.invalid_input'
                ? { message: 'error.invalid_email' }
                : { rawMessage: message }
            );
          } catch {
            onError({ message: 'error.unknown' });
          }
        } else if (error instanceof TimeoutError) {
          onError({ message: 'error.timeout' });
        } else {
          onError({ message: 'error.unknown' });
        }

        return;
      }

      const { identifier } = payload;
      setForgotPasswordIdentifierInputValue(identifier);
      navigate(`/${UserFlow.ForgotPassword}/reset`, { replace: true });
    },
    [
      asyncIdentifyForgotPasswordWithOneTimeToken,
      navigate,
      onError,
      setForgotPasswordIdentifierInputValue,
    ]
  );

  useEffect(() => {
    if (isAutoSubmitted.current) {
      return;
    }

    if (loginHint.length === 0) {
      return;
    }

    // React ref guards one-time auto submit.
    isAutoSubmitted.current = true;
    setIsAutoSubmitting(true);
    void (async () => {
      try {
        await submit({
          token,
          identifier: { type: SignInIdentifier.Email, value: loginHint },
        });
      } finally {
        setIsAutoSubmitting(false);
      }
    })();
  }, [loginHint, submit, token]);

  const isVerificationPending = isSubmitting || isAutoSubmitting;
  const trimmed = email.trim();

  const onSubmitHandler = useCallback(async () => {
    if (isVerificationPending || trimmed.length === 0) {
      return;
    }

    setInvalidMessage(undefined);

    const validationError = validateIdentifierField(SignInIdentifier.Email, trimmed);

    if (validationError) {
      setInvalidMessage(
        typeof validationError === 'string'
          ? t(`error.${validationError}`)
          : t(`error.${validationError.code}`, validationError.data ?? {})
      );
      return;
    }

    setIsSubmitting(true);
    try {
      await submit({ token, identifier: { type: SignInIdentifier.Email, value: trimmed } });
    } finally {
      setIsSubmitting(false);
    }
  }, [isVerificationPending, trimmed, t, submit, token]);

  // A hint means the token is being verified for the user; never show the form.
  if (loginHint) {
    return <LoadingLayer />;
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void onSubmitHandler();
      }}
    >
      <Stack gap={20}>
        <IdentifierField
          autoFocus
          identifier={SignInIdentifier.Email}
          value={email}
          errorMessage={invalidMessage}
          onChange={(next) => {
            setInvalidMessage(undefined);
            setEmail(next);
          }}
        />
        {/* No onClick: an onClick here would run the submit a second time per click. */}
        <Button
          htmlType="submit"
          title={t('action.continue')}
          isLoading={isVerificationPending}
          isDisabled={trimmed.length === 0}
        />
      </Stack>
    </form>
  );
};

/* ------------------------------------------- the self-service reset-code form */

type ResetCodeFormProps = {
  identifier: VerificationCodeIdentifier;
  enabledMethods: VerificationCodeIdentifier[];
};

/** RestoID restatement of `src/pages/ForgotPassword/ForgotPasswordForm`. */
const ResetCodeForm = ({ identifier, enabledMethods }: ResetCodeFormProps) => {
  const { t } = useTranslation();
  const c = useCopy();
  const { errorMessage, clearErrorMessage, onSubmit } = useSendVerificationCode(
    UserFlow.ForgotPassword
  );
  const { setForgotPasswordIdentifierInputValue } = useContext(UserInteractionContext);
  const { value, setValue, isFilled, submitValue } = useIdentifierValue(
    identifier,
    enabledMethods,
    { isForgotPassword: true }
  );
  const [invalidMessage, setInvalidMessage] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(async () => {
    if (!isFilled || submitting) {
      return;
    }
    clearErrorMessage();
    setInvalidMessage(undefined);

    const validationError = validateIdentifierField(identifier, submitValue);

    if (validationError) {
      setInvalidMessage(
        typeof validationError === 'string'
          ? t(`error.${validationError}`)
          : t(`error.${validationError.code}`, validationError.data ?? {})
      );
      return;
    }

    // Cache or update the forgot password identifier input value.
    setForgotPasswordIdentifierInputValue({ type: identifier, value: submitValue });

    setSubmitting(true);
    try {
      await onSubmit({ identifier, value: submitValue });
    } finally {
      setSubmitting(false);
    }
  }, [
    isFilled,
    submitting,
    clearErrorMessage,
    identifier,
    submitValue,
    t,
    setForgotPasswordIdentifierInputValue,
    onSubmit,
  ]);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <Stack gap={20}>
        <IdentifierField
          autoFocus
          identifier={identifier}
          value={value}
          errorMessage={invalidMessage ?? errorMessage}
          onChange={(next) => {
            setInvalidMessage(undefined);
            setValue(next);
          }}
        />
        <CaptchaBox />
        {/* No onClick: an onClick here would run the submit a second time per click. */}
        <Button
          htmlType="submit"
          title={identifier === SignInIdentifier.Phone ? c('getCode') : t('action.continue')}
          isLoading={submitting}
          isDisabled={!isFilled}
        />
      </Stack>
    </form>
  );
};

/* --------------------------------------------------------------- the screen */

const ResetPasswordLanding = () => {
  const { t } = useTranslation();
  const navigate = useNavigateWithPreservedSearchParams();
  // Must run before any early return: it also strips `one_time_token` and
  // `login_hint` from the address bar.
  const { oneTimeToken, loginHint } = useConsumeOneTimeTokenParameters();
  const [magicLinkError, setMagicLinkError] = useState<ResetPasswordMagicLinkError>();
  const enabledMethods = useResetPasswordMethods();

  const identifier = useMemo(
    () => enabledMethods.find((item) => item === SignInIdentifier.Phone) ?? enabledMethods[0],
    [enabledMethods]
  );

  const backToSignIn = (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <LinkText onClick={() => navigate(`/${experience.routes.signIn}`)}>
        {t('description.back_to_sign_in')}
      </LinkText>
    </div>
  );

  if (magicLinkError) {
    return (
      <ErrorPage
        isNavbarHidden
        title="error.invalid_link"
        message={magicLinkError.message ?? 'error.invalid_link_description'}
        rawMessage={magicLinkError.rawMessage}
      />
    );
  }

  if (oneTimeToken) {
    return (
      <FirstScreenLayout pageMeta={{ titleKey: 'description.reset_password' }}>
        <Stack gap={22}>
          <ScreenHead
            title={t('description.reset_password')}
            sub={t('description.reset_password_magic_link_description')}
          />
          <OneTimeTokenForm
            token={oneTimeToken}
            loginHint={loginHint}
            onError={setMagicLinkError}
          />
          {backToSignIn}
        </Stack>
      </FirstScreenLayout>
    );
  }

  // Fallback to sign-in page.
  if (enabledMethods.length === 0 || !identifier) {
    return <Navigate to={`/${experience.routes.signIn}`} />;
  }

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'description.reset_password' }}>
      <Stack gap={22}>
        <ScreenHead
          title={t('description.reset_password')}
          sub={t('description.reset_password_description', {
            types: enabledMethods.map((method) => t(identifierInputDescriptionMap[method])),
          })}
        />
        <ResetCodeForm identifier={identifier} enabledMethods={enabledMethods} />
        {backToSignIn}
      </Stack>
    </FirstScreenLayout>
  );
};

export default ResetPasswordLanding;
